// ABOUTME: Lets an account holder redeem a one-time password recovery link.
// ABOUTME: Keeps the link token in memory and sends it only to the same-origin authentication endpoint.
import { useState, type FormEvent } from "react";
import { authClient } from "../auth";
import { message, type PublicConfig } from "../api";
import { BrandLogo } from "./BrandLogo";
import { Button, Field, Notice } from "./ui/controls";

export function ResetPasswordPage({
  token,
  config,
  navigate,
  onSessionChange,
}: {
  token: string;
  config: PublicConfig | null;
  navigate: (path: string) => void;
  onSessionChange: () => void;
}) {
  const [password, setPassword] = useState(""),
    [confirmation, setConfirmation] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [done, setDone] = useState(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) {
      setError("The passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await authClient.resetPassword({
        token,
        newPassword: password,
      });
      if (result.error)
        throw new Error(
          result.error.message ??
            "This link is invalid or expired. Request a new link from sign in.",
        );
      setPassword("");
      setConfirmation("");
      setDone(true);
      onSessionChange();
    } catch (reason) {
      setError(message(reason));
    } finally {
      setBusy(false);
    }
  }
  const available = config?.email_auth?.password_recovery_available;
  return (
    <main className="consent-layout">
      <BrandLogo />
      <section className="consent-panel" aria-labelledby="reset-title">
        <h1 id="reset-title">Reset your password</h1>
        {done ? (
          <Notice kind="success">
            Your password was changed and your previous sessions were signed
            out. Sign in with your new password.
          </Notice>
        ) : !token ? (
          <Notice kind="error">
            This link is invalid or expired. Request a new link from sign in.
          </Notice>
        ) : config && !available ? (
          <Notice kind="error">
            Password recovery is unavailable. Try again later.
          </Notice>
        ) : (
          <form className="stack-form" onSubmit={submit}>
            {error && <Notice kind="error">{error}</Notice>}
            <Field label="New password" hint="At least 12 characters">
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label="Confirm new password">
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />
            </Field>
            <Button type="submit" busy={busy} disabled={!available}>
              Save new password
            </Button>
          </form>
        )}
        <Button variant="quiet" onClick={() => navigate("/login")}>
          Back to sign in
        </Button>
      </section>
    </main>
  );
}
